/**
 * 统一的WebSocket管理器
 * 负责管理全局唯一的WebSocket连接，处理消息分发和连接状态管理
 */
import { getCookie } from '@/utils/cookieUtils'

class WebSocketManager {
  constructor() {
    this.socket = null
    this.isConnecting = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectTimeout = null
    this.messageHandlers = new Map() // 消息处理器映射
    this.connectionListeners = new Set() // 连接状态监听器
    this.isManualClose = false // 是否手动关闭
    this.heartbeatTimer = null // 心跳定时器
    this.heartbeatInterval = 30000 // 心跳间隔 30秒
  }

  /**
   * 建立WebSocket连接
   */
  connect() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket已连接，无需重复连接')
      return Promise.resolve()
    }

    if (this.isConnecting) {
      console.log('WebSocket正在连接中...')
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      try {
        const token = getCookie('token')
        if (!token) {
          console.error('无法获取token，无法建立WebSocket连接')
          reject(new Error('未登录'))
          return
        }

        const wsUrl = `${process.env.WS_API}/ws?token=${token}`
        
        // 关闭现有连接
        this.closeConnection()
        
        this.socket = new WebSocket(wsUrl)
        this.isConnecting = true
        this.isManualClose = false

        // 连接成功
        this.socket.onopen = () => {
          console.log('WebSocket连接成功')
          this.isConnecting = false
          this.reconnectAttempts = 0
          this.startHeartbeat()
          this.notifyConnectionListeners('connected')
          resolve()
        }

        // 接收消息
        this.socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            this.handleMessage(message)
          } catch (error) {
            console.error('解析WebSocket消息失败:', error)
          }
        }

        // 连接关闭
        this.socket.onclose = (event) => {
          console.log('WebSocket连接关闭', event)
          this.socket = null
          this.isConnecting = false
          this.stopHeartbeat()
          this.notifyConnectionListeners('disconnected')
          
          // 如果不是手动关闭且未超过重连次数，则自动重连
          if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect()
          }
        }

        // 连接错误
        this.socket.onerror = (error) => {
          console.error('WebSocket连接错误:', error)
          this.isConnecting = false
          this.notifyConnectionListeners('error', error)
          reject(error)
        }

      } catch (error) {
        this.isConnecting = false
        console.error('WebSocket连接异常:', error)
        reject(error)
      }
    })
  }

  /**
   * 安排重连
   */
  scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }

    const delay = 1000 * Math.min(2 ** this.reconnectAttempts, 30) // 指数退避策略
    console.log(`${delay}ms后尝试第${this.reconnectAttempts + 1}次重连`)
    
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++
      this.connect().catch(error => {
        console.error('重连失败:', error)
      })
    }, delay)
  }

  /**
   * 关闭连接
   */
  disconnect() {
    this.isManualClose = true
    this.stopHeartbeat()
    this.closeConnection()
    this.clearReconnectTimeout()
  }

  /**
   * 内部关闭连接方法
   */
  closeConnection() {
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
  }

  /**
   * 清除重连定时器
   */
  clearReconnectTimeout() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
  }

  /**
   * 启动心跳机制
   * 定时发送心跳包保持连接活跃，防止服务端或中间代理断开空闲连接
   */
  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        // 发送心跳包（空字符串或特定格式）
        this.socket.send(JSON.stringify({ messageType: 'ping' }))
      } else {
        this.stopHeartbeat()
      }
    }, this.heartbeatInterval)
  }

  /**
   * 停止心跳
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /**
   * 发送消息
   */
  sendMessage(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message))
      return true
    } else {
      console.warn('WebSocket未连接，无法发送消息')
      return false
    }
  }

  /**
   * 处理接收到的消息
   */
  handleMessage(message) {
    console.log('收到WebSocket消息:', message)
    
    // 根据消息类型分发给对应的处理器
    const messageType = message.messageType || message.type
    
    if (this.messageHandlers.has(messageType)) {
      const handlers = this.messageHandlers.get(messageType)
      handlers.forEach(handler => {
        try {
          handler(message)
        } catch (error) {
          console.error('消息处理器执行错误:', error)
        }
      })
    }

    // 通用消息处理（所有监听器都会收到）
    if (this.messageHandlers.has('*')) {
      const handlers = this.messageHandlers.get('*')
      handlers.forEach(handler => {
        try {
          handler(message)
        } catch (error) {
          console.error('通用消息处理器执行错误:', error)
        }
      })
    }
  }

  /**
   * 注册消息处理器
   * @param {string|number} messageType 消息类型，使用'*'监听所有消息
   * @param {function} handler 处理函数
   * @param {string} handlerId 处理器ID，用于后续移除
   */
  onMessage(messageType, handler, handlerId = null) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set())
    }
    
    // 如果提供了handlerId，将其存储在handler函数上
    if (handlerId) {
      handler._handlerId = handlerId
    }
    
    this.messageHandlers.get(messageType).add(handler)
  }

  /**
   * 移除消息处理器
   * @param {string|number} messageType 消息类型
   * @param {function|string} handlerOrId 处理函数或处理器ID
   */
  offMessage(messageType, handlerOrId) {
    if (!this.messageHandlers.has(messageType)) {
      return
    }

    const handlers = this.messageHandlers.get(messageType)
    
    if (typeof handlerOrId === 'string') {
      // 根据ID移除
      for (const handler of handlers) {
        if (handler._handlerId === handlerOrId) {
          handlers.delete(handler)
          break
        }
      }
    } else {
      // 直接移除处理函数
      handlers.delete(handlerOrId)
    }

    // 如果没有处理器了，删除整个类型
    if (handlers.size === 0) {
      this.messageHandlers.delete(messageType)
    }
  }

  /**
   * 移除组件的所有消息处理器
   * @param {string} componentId 组件ID
   */
  offAllMessages(componentId) {
    for (const [messageType, handlers] of this.messageHandlers) {
      for (const handler of handlers) {
        if (handler._handlerId && handler._handlerId.startsWith(componentId)) {
          handlers.delete(handler)
        }
      }
      
      if (handlers.size === 0) {
        this.messageHandlers.delete(messageType)
      }
    }
  }

  /**
   * 注册连接状态监听器
   */
  onConnectionChange(listener) {
    this.connectionListeners.add(listener)
  }

  /**
   * 移除连接状态监听器
   */
  offConnectionChange(listener) {
    this.connectionListeners.delete(listener)
  }

  /**
   * 通知连接状态监听器
   */
  notifyConnectionListeners(status, data = null) {
    this.connectionListeners.forEach(listener => {
      try {
        listener(status, data)
      } catch (error) {
        console.error('连接状态监听器执行错误:', error)
      }
    })
  }

  /**
   * 获取连接状态
   */
  getConnectionState() {
    if (!this.socket) return 'disconnected'
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting'
      case WebSocket.OPEN:
        return 'connected'
      case WebSocket.CLOSING:
        return 'closing'
      case WebSocket.CLOSED:
        return 'disconnected'
      default:
        return 'unknown'
    }
  }

  /**
   * 检查是否已连接
   */
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN
  }
}

// 创建全局单例
const webSocketManager = new WebSocketManager()

export { webSocketManager }
export default webSocketManager


// WEBPACK FOOTER //
// ./src/utils/WebSocketManager.js